import { useState } from 'react';
import { CarteLienClient } from '../../components/CarteLienClient.jsx';
import { Shell } from '../../components/layout/Shell.jsx';
import { PiecesJointes } from '../../components/PiecesJointes.jsx';
import { SignPad } from '../../components/SignPad.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { ACCESS_ROLE } from '../../lib/access';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Inp } from '../../components/ui/Inputs.jsx';
import { Pill } from '../../components/ui/Pill.jsx';
import { adresseLisible } from '../../lib/adresse';
import { DEVISE, fmt } from '../../lib/currency';
import { echeancePour } from '../../lib/echeances';
import { tauxTvaParDefaut } from '../../lib/tva';
import { ordreDeSaisie } from '../../lib/ordre';
import { nextSeq, today } from '../../lib/utils';
import { C } from '../../theme/palette';
import { factureVersDto } from '../../api/mappers';
import { useReglages } from '../../api/reglages.jsx';
import { envoisApi, facturesApi } from '../../api/resources';
import { trf } from '../../lib/i18n';

export function LivraisonsPage({
  onNav,
  soc,
  livraisons,
  setLivraisons,
  clients,
  factures,
  facturesActives,
  rechargeFactures,
  rechargeStock,
  estMixte,
  univers,
  setUnivers,
}) {
  const estMag = estMixte ? univers === 'magasin' : soc?.secteur === 'magasin';
  const { reglage, setReglage } = useReglages();
  // Création de la facture depuis le bon : en cours, ou refusée par le serveur.
  const [facturation, setFacturation] = useState({ enCours: false, erreur: '' });

  /**
   * Crée au serveur la facture des lignes livrées d'un bon.
   *
   * <p>La facture partait dans la collection des factures de l'écran, qui
   * n'est tenue que pour un rôle dont l'écran Factures est ouvert : posée là
   * par un livreur sans cet écran, elle n'atteignait jamais la base alors que
   * le bon se disait « facturé ». Elle est créée par l'API directement ; la
   * collection, si elle est active, est relue.
   *
   * @param {object} bon bon de livraison signé
   * @param {object[]} lignesFac lignes livrées, à facturer
   * @param {object} [clx] client du bon
   * @returns {Promise<string>} le numéro de la facture créée
   */
  const factureDepuisLeBon = async (bon, lignesFac, clx) => {
    const brouillon = {
      id: nextSeq(factures),
      type: 'standard',
      chantierId: '',
      clientId: bon.clientId,
      date: today(),
      echeance: echeancePour(clx, soc),
      statut: 'Envoyée',
      envoye: true,
      envoyeLe: today(),
      tvaPct: tauxTvaParDefaut(soc),
      lignes: lignesFac,
      sect: 'magasin',
    };
    const creee = await facturesApi.cree(factureVersDto(brouillon));
    if (facturesActives && rechargeFactures) await rechargeFactures();
    // La marchandise livrée et facturée quitte le stock au serveur, avec la
    // facture : le stock affiché se relit.
    if (rechargeStock) await rechargeStock();
    return { numero: (creee && creee.numero) || brouillon.id, id: creee && creee.id };
  };

  /** Message quand le serveur refuse la facture ; le bon reste tel qu'il est. */
  const refusDeFacture = (e) =>
    setFacturation({
      enCours: false,
      erreur:
        (e && e.message) || 'La facture n’a pas pu être créée. Vérifie la connexion et réessaie.',
    });
  const [selId, setSelId] = useState(null);
  // Le patron seul efface une pièce déjà émise ; le serveur le vérifie aussi.
  const estPatron = ACCESS_ROLE === 'patron';
  const [sig, setSig] = useState('');
  const [nom, setNom] = useState('');
  const sel = livraisons.find((l) => l.id === selId);
  const total = (l) =>
    (l.lignes || []).reduce((s, x) => s + x.q * x.p * (1 - (x.rem || 0) / 100), 0);

  if (sel) {
    const cl = clients.find((c) => c.id === sel.clientId);
    return (
      <Shell
        active="plus"
        onNav={onNav}
        soc={soc}
        back
        onBack={() => {
          setSelId(null);
          setSig('');
          setNom('');
        }}
        pageTitle={sel.num || sel.id}
        estMixte={estMixte}
        univers={univers}
        setUnivers={setUnivers}
      >
        <Card style={{ marginBottom: 11 }}>
          <div
            style={{
              background: C.white,
              padding: '12px 14px',
              borderBottom: '3px solid ' + C.green,
            }}
          >
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
            >
              <div>
                <div style={{ fontSize: 8, color: C.muted, textTransform: 'uppercase' }}>
                  Bon de livraison
                </div>
                <div style={{ fontSize: 13, fontWeight: 900 }}>{sel.num || sel.id}</div>
                <div style={{ fontSize: 8.5, color: C.grey }}>{sel.date}</div>
                {/* Le livreur a validé depuis son téléphone : le bureau lit qui et quand. */}
                {sel.livrePar && (
                  <div style={{ fontSize: 8.5, color: C.teal, fontWeight: 700, marginTop: 2 }}>
                    Livré{sel.livreLe ? ' le ' + sel.livreLe : ''} par {sel.livrePar}
                  </div>
                )}
              </div>
              <Pill
                label={sel.statut}
                color={
                  sel.statut === 'Signé'
                    ? C.green
                    : sel.statut === 'Livrée'
                      ? C.teal
                      : sel.statut === 'Partielle'
                        ? C.purple
                        : C.amber
                }
                size={8}
              />
            </div>
          </div>
          {cl && (
            <div
              style={{
                margin: '10px 14px',
                background: C.navyXL,
                borderRadius: 8,
                padding: '9px 11px',
              }}
            >
              <div style={{ fontSize: 7.5, color: C.muted, textTransform: 'uppercase' }}>
                Livré à
              </div>
              <div style={{ fontSize: 11, fontWeight: 700 }}>{cl.nom}</div>
              {adresseLisible(sel.adr) && (
                <div style={{ fontSize: 8.5, color: C.grey }} translate="no">
                  📍 {adresseLisible(sel.adr)}
                </div>
              )}
            </div>
          )}
          <div style={{ padding: '0 14px 14px' }}>
            {(sel.lignes || []).map((l, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '7px 0',
                  borderBottom: '1px solid ' + C.border,
                  fontSize: 10,
                }}
              >
                <span>
                  {l.d} · {l.q} {l.u}
                </span>
                <b>
                  {DEVISE} {fmt(l.q * l.p * (1 - (l.rem || 0) / 100))}
                </b>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 700 }}>Total HT</span>
              <span style={{ fontSize: 13, fontWeight: 900 }}>
                {DEVISE} {fmt(total(sel))}
              </span>
            </div>
          </div>
        </Card>
        {/* Rien à charger sur un bon signé ici ou livré par le livreur. */}
        {sel.statut !== 'Signé' && sel.statut !== 'Livrée' && (
          <Card style={{ marginBottom: 11 }}>
            <Bar
              icon="🚚"
              title="Chargement & livraison (livreur)"
              color={C.teal}
              sub="Cocher le matériel chargé, puis en route"
            />
            <div style={{ padding: 13 }}>
              <Btn
                label="📍 Itinéraire (ouvrir Maps)"
                sm
                color={C.navy}
                full
                onClick={() => {
                  try {
                    window.open(
                      'https://www.google.com/maps/search/?api=1&query=' +
                        encodeURIComponent(sel.adr || ''),
                      '_blank',
                    );
                  } catch (e) {}
                }}
                style={{ marginBottom: 10 }}
              />
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: C.muted,
                  marginBottom: 6,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                }}
              >
                📦 Matériel à charger
              </div>
              {(sel.lignes || []).map((l, i) => {
                // L'etat et le chargement vivent sur la ligne, non dans une
                // table indexee par position : deux lignes qui changent
                // d'ordre echangeaient leurs etats, et rien ne les conservait
                // d'un rechargement a l'autre.
                const ok = l.charge === true;
                const et = l.etat || 'livre';
                const poseSurLaLigne = (patch) =>
                  setLivraisons((p) =>
                    p.map((x) =>
                      x.id === sel.id
                        ? {
                            ...x,
                            lignes: (x.lignes || []).map((y, j) =>
                              j === i ? { ...y, ...patch } : y,
                            ),
                          }
                        : x,
                    ),
                  );
                const setEt = (v) => poseSurLaLigne({ etat: v });
                const nonLiv = et !== 'livre';
                return (
                  <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid ' + C.border }}>
                    <div
                      onClick={() => poseSurLaLigne({ charge: !ok })}
                      style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer' }}
                    >
                      <div
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: 6,
                          border: '2px solid ' + (ok ? C.green : C.border),
                          background: ok ? C.green : '#fff',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 13,
                          fontWeight: 900,
                          flexShrink: 0,
                        }}
                      >
                        {ok ? '✓' : ''}
                      </div>
                      <div
                        style={{
                          flex: 1,
                          fontSize: 10.5,
                          fontWeight: 600,
                          textDecoration: ok || nonLiv ? 'line-through' : 'none',
                          color: nonLiv ? C.red : ok ? C.muted : C.ink,
                        }}
                      >
                        {l.d}
                      </div>
                      <div style={{ fontSize: 10, fontWeight: 800, color: C.navy }}>
                        {l.q} {l.u}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 5, marginTop: 6, marginLeft: 31 }}>
                      {[
                        ['livre', '✓ Livré', C.green],
                        ['rupture', '⚠ Rupture', C.amber],
                        ['retour', '↩ Retour', C.red],
                      ].map(([v, lab, col]) => (
                        <div
                          key={v}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEt(v);
                          }}
                          style={{
                            flex: 1,
                            textAlign: 'center',
                            padding: '5px 4px',
                            borderRadius: 7,
                            fontSize: 8.5,
                            fontWeight: 800,
                            cursor: 'pointer',
                            border: '1px solid ' + (et === v ? col : C.border),
                            background: et === v ? col : '#fff',
                            color: et === v ? '#fff' : C.grey,
                          }}
                        >
                          {lab}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {(() => {
                // Une ligne en rupture ne se charge pas : elle n'est pas la.
                // Exiger qu'elle le soit immobilisait le camion — le livreur
                // ne pouvait plus partir des qu'il manquait un article, et
                // le bon restait « A livrer » alors que le reste attendait
                // sur le chantier.
                const aCharger = (sel.lignes || []).filter((l) => (l.etat || 'livre') === 'livre');
                const all = aCharger.length > 0 && aCharger.every((l) => l.charge === true);
                const done = aCharger.filter((l) => l.charge === true).length;
                return (
                  <div style={{ marginTop: 11 }}>
                    <div
                      style={{
                        fontSize: 8.5,
                        color: C.muted,
                        textAlign: 'center',
                        marginBottom: 7,
                      }}
                    >
                      {done}/{aCharger.length} article(s) chargé(s)
                    </div>
                    {sel.statut !== 'En route' ? (
                      <Btn
                        label={
                          all
                            ? '✓ Tout chargé → En route'
                            : aCharger.length === 0
                              ? 'Aucun article à livrer'
                              : 'Cocher tout le matériel pour partir'
                        }
                        color={C.teal}
                        full
                        disabled={!all}
                        onClick={() =>
                          setLivraisons((p) =>
                            p.map((x) => (x.id === sel.id ? { ...x, statut: 'En route' } : x)),
                          )
                        }
                      />
                    ) : (
                      <div
                        style={{
                          background: C.tealL,
                          borderRadius: 9,
                          padding: '9px 11px',
                          textAlign: 'center',
                          fontSize: 10,
                          fontWeight: 800,
                          color: C.teal,
                        }}
                      >
                        {estMag ? '🚚 En route vers le client' : '🚚 En route vers le chantier'}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </Card>
        )}
        {sel.statut !== 'Signé' ? (
          <Card style={{ marginBottom: 11 }}>
            <Bar
              icon="📦"
              title={
                estMag ? 'Réception de la marchandise' : 'Réception du matériel sur le chantier'
              }
              color={C.navy}
              sub="Photo + signature → facture générée et e-mail au client"
            />
            <div style={{ padding: 13 }}>
              {/*
                La photo est une preuve : c'est ce qui a ete depose sur le
                chantier, au moment de la remise. Elle part au serveur des le
                depot — elle vivait jusqu'ici dans l'etat de l'ecran, et la
                perdre revenait a ne l'avoir jamais prise.
              */}
              <div style={{ marginBottom: 11 }}>
                <PiecesJointes
                  genre="LIVRAISON"
                  documentId={sel.id}
                  titre="📷 PHOTO DU MATÉRIEL LIVRÉ"
                />
              </div>
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: C.muted,
                  marginBottom: 5,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                }}
              >
                ✍ Signature du réceptionnaire
              </div>
              <SignPad value={sig} onChange={setSig} color={C.navy} />
              <Inp
                label="Nom de la personne qui a réceptionné"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                placeholder="M. Dupont"
              />
              <Btn
                label="✓ Livré & signé → facture + e-mail au client"
                color={C.green}
                full
                disabled={!sig || !nom.trim() || facturation.enCours}
                onClick={async () => {
                  const lignesFac = (sel.lignes || []).filter(
                    (l) => (l.etat || 'livre') === 'livre',
                  );
                  const clx = (clients || []).find((c) => c.id === sel.clientId);
                  setFacturation({ enCours: true, erreur: '' });
                  let numF;
                  let idF;
                  try {
                    ({ numero: numF, id: idF } = await factureDepuisLeBon(sel, lignesFac, clx));
                  } catch (e) {
                    // Le bon reste à signer : signature et nom sont conservés,
                    // un nouvel appui réessaie.
                    refusDeFacture(e);
                    return;
                  }
                  setFacturation({ enCours: false, erreur: '' });
                  setLivraisons((p) =>
                    p.map((x) =>
                      x.id === sel.id
                        ? {
                            ...x,
                            // Une livraison amputee n'est pas une livraison :
                            // le bon le dit, et ce qui manque reste a livrer.
                            statut:
                              lignesFac.length === (sel.lignes || []).length
                                ? 'Signé'
                                : 'Partielle',
                            sig,
                            signePar: nom,
                            signeLe: today(),
                            livreLe: today(),
                            livrePar: nom,
                            facture: numF,
                            factureEnvoyee: !!(clx && clx.email),
                          }
                        : x,
                    ),
                  );
                  // La facture part par l'e-mail de SwissTech, PDF joint : le
                  // brouillon de l'app Mail n'avait ni facture ni accents, et
                  // la facture se disait envoyée même si rien ne partait.
                  if (clx && clx.email && idF != null) {
                    try {
                      await envoisApi.envoieFacture(idF);
                    } catch (e) {
                      setLivraisons((p) =>
                        p.map((x) => (x.id === sel.id ? { ...x, factureEnvoyee: false } : x)),
                      );
                      setFacturation({
                        enCours: false,
                        erreur: trf(
                          'La facture {0} est créée, mais l’e-mail n’est pas parti : envoie-la depuis Factures.',
                          numF,
                        ),
                      });
                    }
                  }
                }}
              />
              {(!sig || !nom.trim()) && (
                <div style={{ fontSize: 8, color: C.muted, textAlign: 'center', marginTop: 6 }}>
                  Signature et nom requis. La photo est recommandée.
                </div>
              )}
              {facturation.erreur && (
                <div style={{ fontSize: 9, color: C.red, textAlign: 'center', marginTop: 6 }}>
                  {facturation.erreur}
                </div>
              )}
              <div
                style={{
                  fontSize: 8,
                  color: C.muted,
                  textAlign: 'center',
                  marginTop: 6,
                  lineHeight: 1.5,
                }}
              >
                À la signature : la facture est créée (articles livrés) et l'e-mail au client
                s'ouvre pré-rempli. Si le client n'a pas d'e-mail enregistré, la facture reste dans
                « Factures » à envoyer.
              </div>
            </div>
          </Card>
        ) : (
          <div
            style={{
              background: C.greenL,
              border: '1px solid ' + C.green + '30',
              borderRadius: 10,
              padding: '11px 13px',
              marginBottom: 11,
            }}
          >
            <div style={{ fontSize: 10.5, fontWeight: 800, color: C.green }}>
              ✓ Réceptionné par {sel.signePar} le {sel.signeLe}
            </div>
            <div style={{ marginTop: 8 }}>
              <PiecesJointes genre="LIVRAISON" documentId={sel.id} />
            </div>
            {sel.sig && (
              <svg
                viewBox="0 0 320 120"
                style={{
                  width: '100%',
                  height: 60,
                  marginTop: 8,
                  background: '#fff',
                  border: '1px solid ' + C.green + '30',
                  borderRadius: 8,
                }}
              >
                <path d={sel.sig} fill="none" stroke={C.navy} strokeWidth={2} />
              </svg>
            )}
          </div>
        )}
        {sel.facture ? (
          <div
            style={{
              background: C.tealL,
              border: '1px solid ' + C.teal + '30',
              borderRadius: 9,
              padding: '10px 12px',
              fontSize: 9.5,
              color: C.teal,
              fontWeight: 700,
            }}
          >
            🧾 Facture {typeof sel.facture === 'string' ? sel.facture : ''} générée —{' '}
            {facturesActives === false
              ? 'enregistrée sur le serveur, visible dans Factures pour la direction.'
              : 'disponible dans Factures.'}
            {sel.factureEnvoyee
              ? ' Un courriel au client a été préparé : vérifie qu’il est bien parti.'
              : ''}
          </div>
        ) : (
          // Le bon signé ici, ou livré par le livreur depuis son téléphone
          // (« Livrée », « Partielle ») : dans les deux cas des articles sont
          // partis et se facturent. Réservée au bon signé, la facture d'une
          // tournée validée par l'employé n'avait aucune porte au bureau.
          ['Signé', 'Livrée', 'Partielle'].includes(sel.statut) && (
            <Btn
              label="🧾 Générer la facture + e-mail au client"
              color={C.teal}
              full
              disabled={facturation.enCours}
              onClick={async () => {
                const lignesFac = (sel.lignes || []).filter((l) => (l.etat || 'livre') === 'livre');
                const clx = (clients || []).find((c) => c.id === sel.clientId);
                setFacturation({ enCours: true, erreur: '' });
                let numF;
                let idF;
                try {
                  ({ numero: numF, id: idF } = await factureDepuisLeBon(sel, lignesFac, clx));
                } catch (e) {
                  refusDeFacture(e);
                  return;
                }
                setFacturation({ enCours: false, erreur: '' });
                setLivraisons((p) =>
                  p.map((x) =>
                    x.id === sel.id
                      ? { ...x, facture: numF, factureEnvoyee: !!(clx && clx.email) }
                      : x,
                  ),
                );
                // La facture part par l'e-mail de SwissTech, PDF joint.
                if (clx && clx.email && idF != null) {
                  try {
                    await envoisApi.envoieFacture(idF);
                  } catch (e) {
                    setLivraisons((p) =>
                      p.map((x) => (x.id === sel.id ? { ...x, factureEnvoyee: false } : x)),
                    );
                    setFacturation({
                      enCours: false,
                      erreur: trf(
                        'La facture {0} est créée, mais l’e-mail n’est pas parti : envoie-la depuis Factures.',
                        numF,
                      ),
                    });
                  }
                }
              }}
            />
          )
        )}
        {facturation.erreur && ['Signé', 'Livrée', 'Partielle'].includes(sel.statut) && (
          <div style={{ fontSize: 9, color: C.red, textAlign: 'center', marginTop: 6 }}>
            {facturation.erreur}
          </div>
        )}
        <CarteLienClient
          type="livraison"
          id={sel.id}
          titre="Suivi de la livraison"
          clientNom={cl?.nom}
          email={cl?.email}
          tel={(cl?.tel || '').replace(/[^0-9]/g, '').replace(/^0/, '41')}
          sujet="Suivi de votre livraison"
          message={(lien) =>
            'Bonjour ' +
            (cl?.nom || '') +
            ', suivez votre livraison ' +
            (sel.num || sel.id) +
            ' : ' +
            lien
          }
        >
          <PiecesJointes genre="LIVRAISON" documentId={sel.id} titre="📷 PHOTO DE LA LIVRAISON" />
        </CarteLienClient>
        {/*
          Le bon se supprimait au serveur, mais aucun écran ne le proposait. Un
          bon facturé, le patron seul l'efface ; sa facture, elle, reste.
        */}
        {(!sel.facture || estPatron) && (
          <div style={{ marginTop: 12 }}>
            <Btn
              label="🗑 Supprimer ce bon de livraison"
              ghost
              danger
              color={C.red}
              full
              onClick={() => {
                const question = sel.facture
                  ? 'Ce bon est déjà facturé (' +
                    sel.facture +
                    '). La facture reste.\n\nSupprimer définitivement le bon ?'
                  : 'Supprimer définitivement ce bon de livraison ?';
                if (!window.confirm(question)) return;
                setLivraisons((p) => p.filter((x) => x.id !== sel.id));
                setSelId(null);
              }}
            />
          </div>
        )}
      </Shell>
    );
  }

  return (
    <Shell
      active="plus"
      onNav={onNav}
      soc={soc}
      pageTitle="🚚 Bons de livraison"
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      {livraisons.length === 0 && (
        <Card>
          <div style={{ padding: 20, textAlign: 'center', color: C.muted, fontSize: 11 }}>
            Aucun bon de livraison
          </div>
        </Card>
      )}
      {ordreDeSaisie(livraisons).map((l) => {
        const cl = clients.find((c) => c.id === l.clientId);
        const col =
          l.statut === 'Signé'
            ? C.green
            : l.statut === 'Livrée'
              ? C.teal
              : l.statut === 'Partielle'
                ? C.purple
                : C.amber;
        return (
          <Card
            key={l.id}
            style={{ marginBottom: 9, cursor: 'pointer', borderLeft: '4px solid ' + col }}
            onClick={() => {
              setSelId(l.id);
              setSig(l.sig || '');
              setNom(l.signePar || '');
            }}
          >
            <div
              style={{
                padding: '12px 14px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontSize: 11, fontWeight: 800 }}>{l.num || l.id}</div>
                <div style={{ fontSize: 10, color: C.grey }}>
                  {cl?.nom || (l.demandePar ? '🧰 Demande ' + l.demandePar : '')}
                </div>
                {adresseLisible(l.adr) && (
                  <div style={{ fontSize: 8, color: C.muted }} translate="no">
                    📍 {adresseLisible(l.adr)}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <Pill label={l.statut} color={col} size={8} />
                {l.facture && (
                  <div style={{ fontSize: 7.5, color: C.teal, marginTop: 3 }}>🧾 facturé</div>
                )}
              </div>
            </div>
          </Card>
        );
      })}
    </Shell>
  );
}
